import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { UnpaidBreak } from '../../enums/unpaid-break.enum';
import * as moment from 'moment';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
})
export class CalendarComponent implements OnInit {

  @Input() calendarId: string = '';
  @Input() selectedDates = [];
  @Input() mode: 'single' | 'multiple' = 'single';
  @Input() tz: string = 'America/Toronto';
  @Output() dateChange = new EventEmitter();
  resizeObserver: any;

  isPrevMonthDisabled: boolean = false;
  displayMonthCount: number = 2;
  public currentDate = moment().tz(this.tz);
  public currentMonthIndex = 0;
  suppressEmit = false;   // Flag to prevent emitting during @Input change handling

  calendarMonths: {
    month: string;
    year: string;
    dates: any[];
  }[] = [];

  selectedShifts = [];


  constructor() { 
    this.selectedShifts = [...this.selectedDates];
      // Set displayMonthCount based on screen width
      
      if (window.innerWidth < 1000) {
        this.displayMonthCount = 1;
      }

      this.resizeObserver = () => this.adjustDisplayMonthCount();
      window.addEventListener('resize', this.resizeObserver);

    
    // this.generateCalendarMonths();
  }

  ngOnInit(): void {
      // Set displayMonthCount on initialization based on screen width
    this.adjustDisplayMonthCount();

    this.selectedShifts = [...this.selectedDates];
   // Select default date on component load
    this.selectInitialDate();
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeObserver);
  }

  adjustDisplayMonthCount() {
  this.displayMonthCount = window.innerWidth < 1000 ? 1 : 2;
  console.log('this.displayMonthCount: ', this.displayMonthCount, window.innerWidth);
  this.generateCalendarMonths(); // Refresh calendar view
}
  

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedDates'] && !this.areDatesEqual(changes['selectedDates'].currentValue, this.selectedShifts)) {
      this.suppressEmit = true;
      this.selectedShifts = [...this.selectedDates];
      this.suppressEmit = false;
    }
  }

  areDatesEqual(arr1, arr2) {
    return JSON.stringify(arr1) === JSON.stringify(arr2);
  }

  selectInitialDate() {
    if (!this.selectedShifts?.length) {
      const today = moment().tz(this.tz).format('MM/DD/YYYY');
      this.selectedShifts.push({
        shiftDate: today,
      });

      const month = moment().tz(this.tz).format('MMMM');
      const date = +moment().tz(this.tz).format('DD');
      this.handleDateClick(month, date);
    }
  
    this.generateCalendarMonths();
  
  }
  
  getFirstDayOffset(month: string, year: string): number {
    return moment(`${month} 1 ${year}`, 'MMMM D YYYY').day();
  }
  
  // Used to render blank spaces at the start of the calendar grid
  getOffsetArray(month: string, year: string): any[] {
    const offset = this.getFirstDayOffset(month, year);
    return Array(offset).fill(null);
  }
  
  // Generate two months of calendar data dynamically
  generateCalendarMonths() {
    this.calendarMonths = [];
    const startMonth = this.currentDate.clone().add(this.currentMonthIndex, 'months');


    for (let offset = 0; offset < this.displayMonthCount; offset++) {
      const refDate = startMonth.clone().add(offset, 'months');
    
      // Skip rendering if the month is before the current month
      if (refDate.isBefore(moment().startOf('month'))) {
        continue;
      }

      const month = refDate.format('MMMM');
      const year = refDate.format('YYYY');
      const dates = this.getDatesForMonth(month, year);
      
      this.calendarMonths.push({ month, year, dates });

      this.checkCurrentMonth()
    }
  }

  checkCurrentMonth() {
 // Update the disabled state of the left arrow based on the current month
  const currentMonth = this.currentDate.month();
  const currentYear = this.currentDate.year();
  const displayedMonth = this.currentDate.clone().add(this.currentMonthIndex, 'months').month();
  const displayedYear = this.currentDate.clone().add(this.currentMonthIndex, 'months').year();

  this.isPrevMonthDisabled = currentMonth === displayedMonth && currentYear === displayedYear;

  }

  // Generate all dates in a given month
  getDatesForMonth(month: string, year: string): any[] {
    const monthNumber = moment(`${month} ${year}`, 'MMMM YYYY').month();
    const daysInMonth = moment({ year: +year, month: monthNumber }).daysInMonth();
    const today = moment().tz(this.tz).startOf('day');

    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = i + 1;
      const fullDate = moment(`${month} ${date} ${year}`, 'MMMM D YYYY').tz(this.tz).startOf('day');
      return {
        date: fullDate,
        disabled: fullDate.isBefore(today)
      }
    });
  }


  nextMonth() {
    this.currentMonthIndex += this.displayMonthCount;
    this.generateCalendarMonths();
  }

  prevMonth() {

    if(this.isPrevMonthDisabled) {
      return;
    }
    
    this.currentMonthIndex -= this.displayMonthCount;
    this.generateCalendarMonths();
  }

  // Highlights selected days in UI
  isDateSelected(month: string, date: number): boolean {
    const year = this.calendarMonths.find(m => m.month === month)?.year || this.currentDate.year().toString();
    const fullDate = moment(`${month} ${date} ${year}`, 'MMMM D YYYY').tz(this.tz).format('MM/DD/YYYY');
  
    return this.selectedShifts.some((shift) => shift.shiftDate === fullDate);
  }
  
  
  // Handles user click on a day
  handleDateClick(month: string, date: number) {
    const year = this.calendarMonths.find(m => m.month === month)?.year || this.currentDate.year().toString();
    const fullDate = moment(`${month} ${date} ${year}`, 'MMMM D YYYY').tz(this.tz).format('MM/DD/YYYY');
  
    if (this.mode === 'single') {
      this.selectedShifts = [{
        shiftDate: fullDate
      }];
    } else {
      const existingIndex = this.selectedShifts.findIndex((s) =>
        s.shiftDate === fullDate
      );
      // Toggle selection for the clicked day in multiple mode
      if (existingIndex > -1) {
        this.selectedShifts.splice(existingIndex, 1);
      } else {
        this.selectedShifts.push({
          shiftDate: fullDate
        });
      }
    }
  
    // Emit selection change only if not internally triggered
    setTimeout(() => {
      if (!this.suppressEmit) {
        this.dateChange.emit([...this.selectedShifts]);
      }
    }, 0);
  }
  
}
